import { logError } from "@gettactic/helpers";
import { loadBadgeImage } from "./badge-image-loader";
import { resolveBadgeTheme } from "./badge-themes/registry";
import type {
  BadgeInkMode,
  BadgeOrientation,
  BadgeTheme
} from "./badge-themes/types";

const DPI = 300;
const MM_PER_INCH = 25.4;
// Default badge: 4x3 inches, used when the printer does not report a label size.
export const DEFAULT_BADGE_WIDTH_MM = 101.6;
export const DEFAULT_BADGE_HEIGHT_MM = 76.2;

const mmToPx = (mm: number): number => Math.round((mm / MM_PER_INCH) * DPI);

/** What the stations app reports about how badges are printed. */
export type BadgePrinterType = "none" | "generic" | "brother";

/**
 * Thermal label printers (Brother) are one-bit, so they get the mono ink
 * mode. Older stations builds do not report a printer type and only ever
 * drove Brother printers, so a missing type also means mono.
 */
export const inferBadgeInkMode = (
  printerType?: BadgePrinterType | null
): BadgeInkMode => (printerType === "generic" ? "colour" : "mono");

export interface BadgeCanvasPlanInput {
  widthMm: number;
  heightMm: number;
  isContinuous: boolean;
  orientation: BadgeOrientation;
  /** From the theme: feed length for continuous stock of the given width. */
  continuousLengthMm: (stockWidthMm: number) => number;
}

export interface BadgeCanvasPlan {
  /** Physical label size, which the canvas always matches. */
  widthMm: number;
  heightMm: number;
  /** Size of the drawing area the theme sees. */
  logicalWidthMm: number;
  logicalHeightMm: number;
  /** True when the context must be rotated 90° so the logical area fits the label. */
  isRotated: boolean;
}

/**
 * Decide the physical canvas and the logical drawing area.
 *
 * "Landscape" means the badge reads along the label's long side, "portrait"
 * along the short one, whichever way the printer feeds the stock. The
 * context is rotated only when the label's own orientation differs — a
 * 4×3in badge in landscape is drawn as-is, while a 62×100 mm Brother label
 * in landscape is drawn sideways so it reads correctly once turned.
 */
export const planBadgeCanvas = (
  input: BadgeCanvasPlanInput
): BadgeCanvasPlan => {
  const widthMm = input.widthMm;
  // Continuous rolls only have a width; the theme decides how long the badge is.
  const heightMm = input.isContinuous
    ? input.continuousLengthMm(widthMm)
    : input.heightMm;
  const longMm = Math.max(widthMm, heightMm);
  const shortMm = Math.min(widthMm, heightMm);
  const isLandscape = input.orientation === "landscape";
  const logicalWidthMm = isLandscape ? longMm : shortMm;
  const logicalHeightMm = isLandscape ? shortMm : longMm;
  return {
    widthMm,
    heightMm,
    logicalWidthMm,
    logicalHeightMm,
    isRotated: logicalWidthMm !== widthMm
  };
};

export interface BadgeRenderOptions {
  /** Defaults to the registry's default theme. */
  theme?: BadgeTheme;
  /** Defaults to mono (thermal printers). */
  inkMode?: BadgeInkMode;
  visitorName: string;
  visitorType?: string | null;
  hostName?: string | null;
  dateString: string;
  /** Visitor photo as a data URI or URL. Omitted when none was captured. */
  photoUrl?: string | null;
  /** Organization logo URL. Themes fall back to the Tactic lockup. */
  logoUrl?: string | null;
  targetWidthMm?: number;
  targetHeightMm?: number;
  isRound?: boolean;
  isContinuous?: boolean;
  orientation?: BadgeOrientation;
}

// Fonts the themes draw with. Loaded through the app's Google Fonts links;
// the canvas cannot trigger the download on its own.
const BADGE_FONTS = [
  '700 40px "Mona Sans"',
  "600 20px Inter",
  "500 20px Inter"
];

const ensureBadgeFontsLoaded = async (): Promise<void> => {
  if (typeof document === "undefined" || !document.fonts?.load) return;
  try {
    await Promise.all(BADGE_FONTS.map((font) => document.fonts.load(font)));
  } catch (error) {
    // A missing font only degrades to the system fallback; the badge still prints.
    logError("Badge fonts failed to load", error);
  }
};

/**
 * Render a visitor badge to a canvas and return it as a base64 PNG data URI.
 * The canvas always matches the physical label size at 300 dpi; the chosen
 * theme draws the content. A photo or logo that fails to load is skipped so
 * the badge still prints.
 */
export const renderBadgeToBase64 = async (
  opts: BadgeRenderOptions
): Promise<string> => {
  const theme = opts.theme ?? resolveBadgeTheme();
  const [photo, logo] = await Promise.all([
    loadBadgeImage(opts.photoUrl),
    loadBadgeImage(opts.logoUrl),
    ensureBadgeFontsLoaded()
  ]);

  const plan = planBadgeCanvas({
    widthMm: opts.targetWidthMm ?? DEFAULT_BADGE_WIDTH_MM,
    heightMm: opts.targetHeightMm ?? DEFAULT_BADGE_HEIGHT_MM,
    isContinuous: opts.isContinuous ?? false,
    orientation: opts.orientation ?? "landscape",
    continuousLengthMm: theme.continuousLengthMm
  });
  const isRound = opts.isRound ?? false;

  const canvas = document.createElement("canvas");
  canvas.width = mmToPx(plan.widthMm);
  canvas.height = mmToPx(plan.heightMm);
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // Rotating 90° CW maps the logical x axis down the label's long side.
  if (plan.isRotated) {
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
  }
  const width = mmToPx(plan.logicalWidthMm);
  const height = mmToPx(plan.logicalHeightMm);

  if (isRound) {
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
    ctx.clip();
  }
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);

  theme.render(
    ctx,
    {
      width,
      height,
      pxPerMm: DPI / MM_PER_INCH,
      orientation: opts.orientation ?? "landscape",
      isRound
    },
    {
      visitorName: opts.visitorName,
      visitorType: opts.visitorType ?? null,
      hostName: opts.hostName ?? null,
      dateString: opts.dateString,
      photo,
      logo,
      inkMode: opts.inkMode ?? "mono"
    }
  );

  try {
    return canvas.toDataURL("image/png");
  } catch (error) {
    logError("Badge canvas could not be exported", error);
    return "";
  }
};
